import { HttpClient } from '@angular/common/http';
import {
  Component,
  computed,
  ElementRef,
  EventEmitter,
  OnDestroy,
  OnInit,
  Output,
  signal,
  ViewChild,
} from '@angular/core';
import { MatMenuTrigger } from '@angular/material/menu';
import { MatSelectChange } from '@angular/material/select';
import { NavigationEnd, Router } from '@angular/router';
import { interval, lastValueFrom, map, Subject, takeUntil } from 'rxjs';
import { companyDataKey, portalsDataKey } from 'src/app/core/constants/constants';
import { CrossAccount } from 'src/app/core/models/cross-account';
import { DataService } from 'src/app/core/services/data.service';
import { Themes, ThemeService } from 'src/app/core/services/theme.service';
import { PermissionsService } from 'src/app/core/services/permissions.service';
import { UserDataService } from 'src/app/core/services/user-data.service';
import { allowedGlobalSearchServiceAddon } from 'src/app/features/user-permissions/models/service-addon';
import { getTimeDifferenceLabel } from 'src/app/shared/utils/get-difference-time-label';
import { environment } from 'src/environments/environment';
import { CustomerPortal } from '../../models/customer-portal';
import { TicketsSearchResult } from '../../models/tickets-search-result';
import { INotificationMapped, IUserNotification } from '../../models/user-notification';
import { HeaderService } from '../../services/header.service';
import { ThreadMessengerService } from '../../services/thread-messenger.service';
import { UserSessionService } from 'src/app/core/services/user-session.service';
import { FeaturesFlagsV2Service } from 'src/app/core/services/features-flags-v2.service';

@Component({
  selector: 'v2-header',
  templateUrl: './v2-header.component.html',
  styleUrls: ['../../styles/header-styles.scss'],
  standalone: false,
})
export class V2HeaderComponent implements OnInit, OnDestroy {
  /** Subject to destroy the component when it is not needed */
  private destroy$ = new Subject<void>();

  /** Element reference to the universal seach input */
  @ViewChild('universalSearch')
  universalSearchElementRef: ElementRef;

  /** Reference to the trigger used to open search options */
  @ViewChild(MatMenuTrigger)
  searchResultsTrigger: MatMenuTrigger;

  @Output()
  menuClicked = new EventEmitter();

  /** The size of the icons in the navbar */
  readonly iconsSize = 24;

  /** True to show global settings in menu. It only show if user has access to msp or rps */
  isGlobalSettingsEnabled = false;

  /** True while the create redirect token is being created */
  isCreatingRedirectToken = false;

  /** True while a search is in progress */
  isSearching: boolean = false;

  // True if current page has a customer training material
  customerTraining: boolean = false;

  // True if is fetching customer training
  isFetchingCustomerTraining: boolean = false;

  // Used to check if there is customer training on component load
  isFirstLoad: boolean = true;

  /** True while a the hub in a path different to repair services */
  hideSearchBar: boolean = false;

  /** True to activate the search bar message */
  isSearchMsgActive: boolean = false;

  // Store path to check if the current page has customer training material
  currentPath: string = window.location.pathname;

  // Store customer training files
  customerTrainingFiles: { name: string; url: string; lastModified: string }[] = [];

  // Store customer training files
  customerTrainingVideos: { name: string; url: string; lastModified: string }[] = [];

  // store basepath
  // readonly basePath: string = window.location.origin
  readonly basePath: string = window.location.origin;

  /** The text to be used in the universal search */
  searchText: string = '';

  /** The text to be used search bar message */
  searchMessage: string = '';

  /** The previously  */
  previousSearchText: string = '';

  /** The text to be used to filter the accounts */
  searchAccount: string = '';

  searchResults: TicketsSearchResult[] = [];

  /** The profile data of the user */
  profile = {
    email: '',
    givenName: '',
    familyName: '',
  };

  /** The id of the currently selected account */
  selectedAccountId: number = 0;

  /** The availble accounts to which the user can switch */
  crossAccounts: CrossAccount[] = [];

  showGlobalSearch: boolean = false;

  currentTheme: Themes = 'auto';

  get allowPasswordReset(): boolean {
    const account = this.userSessionService.getActiveAccount();
    return account?.idTokenClaims?.['idp'] === 'none';
  }

  get currentActiveItem(): string {
    let result = '';
    result = this.router.url.includes('/secured/orders') ? 'orders' : result;
    result = this.router.url.includes('/secured/support') ? 'support' : result;
    return result;
  }

  /** True if the user can switch accounts */
  get canSwitchAccounts(): boolean {
    const numberOfCrossAccounts = this.userDataService.user?.crossAccounts?.length ?? 0;
    return numberOfCrossAccounts > 1 && this.permissionsService.hasCompanyAdminPermissions();
  }

  /** Returns "Business Unit | Site", "Site", or null depending on what CW contact data is available */
  get locationContext(): string | null {
    const site = this.userDataService.user?.cwSiteName;
    const businessUnit = this.userDataService.user?.cwBusinessUnitName;
    if (businessUnit && site) return `${businessUnit} | ${site}`;
    if (site) return site;
    return null;
  }

  /** True if the tenant options can be displayed for this user */
  tenantOptions: boolean = false;

  /** True if the help center is open */
  isHelpCenterOpen: boolean;

  /** True if the notifications are open */
  areNotificationsOpen: boolean;

  /** True if the udt logo should be showed */
  isHeaderLogoShowed: boolean = false;

  /** List to store the notifications */
  notifications = signal<INotificationMapped[]>([]);

  /** Total number of unread notifications */
  totalUnreadNotifications = computed(() => this.notifications().filter((notification) => !notification.isRead).length);

  /** Hub Environment */
  hubEnvironment: string = environment.environment;

  isBusinessUnitEnabled = false;

  get isSuperAdmin(): boolean {
    return this.permissionsService.isSuperAdmin();
  }

  constructor(
    private router: Router,
    private http: HttpClient,
    private userSessionService: UserSessionService,
    private userDataService: UserDataService,
    private dataService: DataService,
    private headerService: HeaderService,
    private themeService: ThemeService,
    private threadMessengerService: ThreadMessengerService,
    private permissionsService: PermissionsService,
    private featuresFlagsV2Service: FeaturesFlagsV2Service,
  ) {
    this.featuresFlagsV2Service.featuresFlags$.subscribe((flags) => {
      this.isBusinessUnitEnabled = flags.some((f) => f.feature === 'business-unit' && f.isActive);
      this.tenantOptions = this.permissionsService.hasCompanyAdminPermissions();
      this.renderGlobalSettings();
    });
  }

  openHubMetrics() {
    window.open(
      'https://lookerstudio.google.com/u/0/reporting/49d34f3c-6be8-485a-826b-306374266be3/page/L1Q',
      '_blank',
    );
  }

  /** Called when the menu button is clicked */
  clickMenu() {
    this.menuClicked.emit();
  }

  ngOnInit() {
    this.tenantOptions = this.permissionsService.hasCompanyAdminPermissions();

    this.currentTheme = this.themeService.getTheme();

    const account = this.userSessionService.getActiveAccount();

    if (account !== null && Object.keys(account).length > 0) {
      const email = this.userDataService.user.email as string;
      this.profile = {
        givenName: (this.userDataService.user.fullName.split(' ')[0] as string) ?? email?.charAt(0) ?? 'U',
        email: email ?? '',
        familyName: (this.userDataService.user.fullName.split(' ')[1] as string) ?? email?.charAt(1) ?? 'N',
      };
    } else {
      this.profile = null;
    }

    this.crossAccounts = [...this.userDataService.user.crossAccounts];
    this.crossAccounts.sort((accountA, accountB) => accountA.description.localeCompare(accountB.description));
    this.selectedAccountId = this.userDataService.company.id;

    if (location.href.includes('support')) {
      this.hideSearchBar = true;
    }

    this.router.events.subscribe((val) => {
      if (val instanceof NavigationEnd) {
        this.hideSearchBar = val.url.includes('support');
      }
    });

    this.userDataService.getUser$().subscribe((user) => {
      if (!user) {
        this.currentTheme = this.themeService.getTheme();
      }
    });

    this.dataService.isCollapsedSidenav$.subscribe((data) => {
      this.isHeaderLogoShowed = data;
    });

    this.headerService.isHelpCenterOpen$.subscribe((isOpen) => {
      this.isHelpCenterOpen = isOpen;
    });

    this.headerService.areNotificationsOpen$.subscribe((isOpen) => {
      this.areNotificationsOpen = isOpen;
    });

    this.showGlobalSearch = this.shouldShowGlobalSearch();

    // Initial fetch of notifications
    this.fetchNotifications();

    this.renderGlobalSettings();

    // Set up interval to call fetchNotifications every 60 seconds (60000ms)
    interval(60000)
      .pipe(takeUntil(this.destroy$)) // stops interval when component is destroyed
      .subscribe(() => this.fetchNotifications());
  }

  ngOnDestroy() {
    // Clean up the interval by completing the destroy subject
    this.destroy$.next();
    this.destroy$.complete();
  }

  async onSelectedAccountChange(change: MatSelectChange) {
    const previousAccountId = this.userDataService.company.id;
    this.userDataService.isLoadingCustomerInfo = true; // This flag should cause the system to reload everything
    this.userDataService.isLoadingCustomerInfo = true;
    if (sessionStorage.getItem(companyDataKey)) {
      sessionStorage.removeItem(companyDataKey);
    }
    const userId = this.userDataService.user.id;

    try {
      await lastValueFrom(this.http.patch(`${environment.restUrl}/v1/users/${userId}`, { accountId: change.value }));
      const url = new URL(window.location.href);
      url.search = '';

      // If on a Pulse host detail route, redirect to the host list so we don't
      // try to load a host ID that doesn't belong to the newly selected company.
      const pulseHostDetailRegex = /\/pulse\/hosts\/[^/]+(\/.*)?$/;
      if (pulseHostDetailRegex.test(url.pathname)) {
        url.pathname = url.pathname.replace(pulseHostDetailRegex, '/pulse/hosts');
      }

      window.location.href = url.toString();
    } catch (err) {
      this.selectedAccountId = previousAccountId;
      console.error(err);
      this.userDataService.isLoadingCustomerInfo = false;
      this.userDataService.isLoadingCustomerInfo = false;
    }
  }

  logOut() {
    this.threadMessengerService.destroy();
    this.userSessionService.logout();
  }

  /**
   * TODO: Determine if password reset is to be removed
   */
  passwordReset() {
    this.userSessionService.passwordReset();
  }

  shouldShowGlobalSearch(): boolean {
    const companyServiceAddons = this.userDataService.company.serviceAddons ?? [];
    return allowedGlobalSearchServiceAddon.some((allowedServiceAddon) =>
      companyServiceAddons.includes(allowedServiceAddon),
    );
  }

  openHelpCenter() {
    this.headerService.toggleHelpCenter(true);
  }

  closeHelpCenter(open: boolean) {
    this.headerService.toggleHelpCenter(open);
  }

  openNotifications() {
    this.headerService.toggleNotifications(true);
  }

  closeNotifications(open: boolean) {
    this.headerService.toggleNotifications(open);
  }

  onThemeChanged(theme: Themes) {
    this.themeService.setTheme(theme);
    this.currentTheme = theme;
  }

  /** Fetch the notifications for the user */
  private fetchNotifications() {
    const portals: CustomerPortal[] = JSON.parse(sessionStorage.getItem(portalsDataKey));

    this.http
      .get<IUserNotification[]>(`${environment.restUrl}/v1/hub/user-notifications`)
      .pipe(
        map((notifications) => {
          return notifications.map((notification) => ({
            id: notification.id,
            description: notification.description,
            createdOn: getTimeDifferenceLabel(notification.createdOn),
            isRead: notification.isRead,
            redirectedUrl: notification.redirectedUrl,
            title: portals.find((portal) => portal.addonName === notification.serviceAddon)?.name,
            icon: portals.find((portal) => portal.addonName === notification.serviceAddon)?.icon,
          }));
        }),
      )
      .subscribe((notifications) => {
        this.notifications.set([...notifications]);
      });
  }

  /** Detect the changes on the accounts input and filters the values in the dropdown */
  onSearchChange() {
    this.crossAccounts = this.userDataService.user.crossAccounts
      .filter(
        (account) =>
          !this.searchAccount || account.description.toLowerCase().includes(this.searchAccount.toLowerCase()),
      )
      .sort((accountA, accountB) => accountA.description.localeCompare(accountB.description));
  }

  /** Reset the search state when the accounts dropdown closes */
  onSelectOpenedChange(isOpen: boolean) {
    if (!isOpen) {
      this.searchAccount = '';
      this.crossAccounts = [...this.userDataService.user.crossAccounts].sort((a, b) =>
        a.description.localeCompare(b.description),
      );
    }
  }

  /** Decide to show or not the global settings
   * RBAC v2: visible for Customer Admin or Super Admin roles
   * RBAC v1: visible for Global Admin or Super Admin flags
   * Both branches are handled inside PermissionsService.
   */
  renderGlobalSettings() {
    this.isGlobalSettingsEnabled = this.permissionsService.isCustomerAdmin() || this.permissionsService.isSuperAdmin();
  }
}
